import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Grid, 
  CircularProgress,
  useTheme,
  useMediaQuery,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { projectAPI, Employee } from '../services/api';

const ProjectEmployeeAdd: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState<boolean>(true);
  const [projectName, setProjectName] = useState<string>('');
  const [availableEmployees, setAvailableEmployees] = useState<Employee[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<number[]>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    if (id) {
      fetchAvailableEmployees();
    }
  }, [id]);

  const fetchAvailableEmployees = async () => {
    try {
      setLoading(true);
      // ในสถานการณ์จริงควรเรียก API ที่ถูกต้อง
      const response = await fetch('/mocks/projectEmployees.json');
      const data = await response.json();
      setAvailableEmployees(data.availableEmployees);
      setProjectName(data.projectEmployees.projectName);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching available employees:', error);
      setLoading(false);
    }
  };

  const handleSearch = () => {
    // ในสถานการณ์จริงควรเรียก API ค้นหา
    if (searchKeyword.trim() === '') {
      fetchAvailableEmployees();
      return;
    }

    const filteredEmployees = availableEmployees.filter(
      (employee) =>
        employee.name.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        employee.employeeCode.toLowerCase().includes(searchKeyword.toLowerCase()) ||
        employee.position.toLowerCase().includes(searchKeyword.toLowerCase())
    );
    setAvailableEmployees(filteredEmployees);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSelectEmployee = (id: number) => {
    setSelectedEmployees((prev) => {
      if (prev.includes(id)) {
        return prev.filter((empId) => empId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = availableEmployees.map((employee) => employee.id);
      setSelectedEmployees(newSelected);
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBack = () => {
    navigate(`/projects/${id}/manage`);
  };

  const handleAddEmployees = async () => {
    if (selectedEmployees.length === 0) {
      setSnackbarMessage('กรุณาเลือกพนักงานอย่างน้อย 1 คน');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setLoading(true);
      // ในสถานการณ์จริงควรเรียก API เพิ่มพนักงาน
      // await projectAPI.addEmployeesToProject(id, selectedEmployees);
      
      // จำลองการเรียก API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSnackbarMessage('เพิ่มพนักงานเข้าโครงการเรียบร้อยแล้ว');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setLoading(false);
      
      // รอให้ Snackbar แสดงสักครู่ก่อนกลับไปหน้าจัดการโครงการ
      setTimeout(() => {
        navigate(`/projects/${id}/manage`);
      }, 1500);
    } catch (error) {
      console.error('Error adding employees to project:', error);
      setSnackbarMessage('เกิดข้อผิดพลาดในการเพิ่มพนักงาน');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const isSelected = (id: number) => selectedEmployees.includes(id);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          เพิ่มรายชื่อพนักงาน
        </Typography>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {projectName}
        </Typography>

        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <TextField
            placeholder="ค้นหาชื่อพนักงาน หรือ รหัสพนักงาน"
            variant="outlined"
            fullWidth
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} edge="end">
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 500 }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
          </Box>
        ) : availableEmployees.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <Typography variant="h6" color="text.secondary">
              ไม่พบข้อมูลพนักงาน
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedEmployees.length > 0 && selectedEmployees.length < availableEmployees.length
                        }
                        checked={selectedEmployees.length === availableEmployees.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell align="center">ลำดับ</TableCell>
                    <TableCell align="center">รหัสพนักงาน</TableCell>
                    <TableCell>ชื่อพนักงาน</TableCell>
                    <TableCell>ตำแหน่ง</TableCell>
                    {!isMobile && <TableCell>เบอร์โทรศัพท์</TableCell>}
                    {!isTablet && <TableCell>อีเมล</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {availableEmployees
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((employee, index) => {
                      const isItemSelected = isSelected(employee.id);
                      return (
                        <TableRow
                          hover
                          onClick={() => handleSelectEmployee(employee.id)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={employee.id}
                          selected={isItemSelected}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={isItemSelected} />
                          </TableCell>
                          <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell align="center">{employee.employeeCode}</TableCell>
                          <TableCell>{employee.name}</TableCell>
                          <TableCell>{employee.position}</TableCell>
                          {!isMobile && <TableCell>{employee.phone}</TableCell>}
                          {!isTablet && <TableCell>{employee.email}</TableCell>}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={availableEmployees.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="แถวต่อหน้า:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
            />
          </>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={handleBack}>
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddEmployees}
          disabled={loading || selectedEmployees.length === 0}
        >
          เพิ่มเข้าโครงการ
        </Button>
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectEmployeeAdd; 